import React from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutPage, LayoutContent, Errors, PhoneNumberLink, SaveAndCompleteLaterButton } from 'components';
import { Text, Stack, Grid, Button } from 'basis';
import { useStoreValue } from 'store';
import { isMobileOnly } from 'react-device-detect';
import { useInitBiometrics, useErrorTrackingAndUpdateStore, useSaveDraft } from 'hooks';
import { DEV_FEATURES } from '__dev/devFeatures';
import { STEP_PROCESSING_BIOMETRICS } from '_config';
import { isFeatureOn } from 'featureToggles';
import { isNZ } from 'utils';
import aplyIdLogo from 'images/applyid-logo.png';
import styled from '@emotion/styled';

const AplyIdLogoImg = styled.img`
  width: 200px;
  height: 40px;
  display: block;
  margin: 8px 0 0 0;
`;

const NumberListItem = styled.li`
  &:not(:first-of-type) {
    margin-top: 24px;
  }
  & :before {
    content: "${p => p.number}";
    display: inline-block;
    text-align: center;
    line-height: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #FFFFFF;
    background-color: #0046AA;
    border-radius: 20px;
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }
`;

const NumberList = styled.ol`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

export const VerifyIdentity = () => {
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Verify now');
  const [store, updateStore] = useStoreValue();
  const history = useHistory();
  const { saveDraft } = useSaveDraft();
  /** Note `isMobileOnly` is excluding tablet devices as APLYiD currently has issues with treating tablets as desktops. After they fix it we can change to use `isMobile` instead */
  const isMobileOrSimulated = isMobileOnly || DEV_FEATURES.SIMULATE_MOBILE;

  const onInitBiometricsSuccess = ({ startUrl }) => {
    updateStore({
      applicationErrors: null,
      activeStep: STEP_PROCESSING_BIOMETRICS,
    });

    if (isMobileOrSimulated) {
      window.location.assign(startUrl);
    } else {
      history.push(`${STEP_PROCESSING_BIOMETRICS}?return=true`);
    }
  };

  const { initBiometrics, loading: startingBiometrics } = useInitBiometrics({
    applicationId: store.applicationId,
    mobileNumber: store.acquisition.contactDetails.mobileNumber,
    onError: handleErrorCallback,
    onSuccess: onInitBiometricsSuccess,
  });

  const onBiometricsVerify = () => {
    updateStore({
      biometricsVerification: 'STARTED',
    });
    if (isFeatureOn('saveAndResume')) {
      saveDraft(store);
    }

    initBiometrics({
      useDesktopFlow: !isMobileOrSimulated,
    });
  };

  if (store.applicationErrors) {
    return (
      <LayoutPage noProgressStepper={isNZ()}>
        <Errors applicationRef={store.applicationRef} />
      </LayoutPage>
    );
  }

  return (
    <LayoutPage noProgressStepper={isNZ()}>
      {isNZ() && (
        <VerifyIdentityNzV2
          onBiometricsVerify={onBiometricsVerify}
          startingBiometrics={startingBiometrics}
          store={store}
        />
      )}
    </LayoutPage>
  );
};

function VerifyIdentityNzV2(props) {
  const { onBiometricsVerify, startingBiometrics, store } = props;
  return (
    <LayoutContent>
      <Stack gap="2">
        <Text>
          Application reference number: <strong>{store.applicationRef}</strong>
        </Text>
        <br />
        <Text textStyle="heading6">We need to verify your identity</Text>
        <Stack gap="4">
          <AplyIdLogoImg src={aplyIdLogo} alt="aply-id" />
          <Text>
            Through our partnership with APLYiD we can verify your identity digitally and it only takes a couple of
            minutes.
          </Text>
          <Text>You can do this with 2 easy steps using your mobile phone:</Text>
          <NumberList>
            <NumberListItem number={1}>
              <Text>Take a photo of your ID document</Text>
            </NumberListItem>
            <NumberListItem number={2}>
              <Text>Take a photo of yourself</Text>
            </NumberListItem>
          </NumberList>
          <Text>Once you've completed the verification process we'll be in contact with you.</Text>
          <Buttons onClick={onBiometricsVerify} loading={startingBiometrics} buttonLabel="Verify now" />
          <Text>
            Alternatively, you can call us on <PhoneNumberLink id="originationsTeam" />
          </Text>
        </Stack>
      </Stack>
    </LayoutContent>
  );
}

const FormButtonWrapper = styled.div`
  & button {
    padding: 12px 16px;
  }
`;

function Buttons({ onClick, loading, buttonLabel }) {
  return (
    <Grid rowsGap="4" colsGap="10" cols="2" margin="4 0 0 0">
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <FormButtonWrapper>
          <Grid>
            <Button onClick={onClick} loading={loading} padding="0 2">
              {buttonLabel}
            </Button>
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
      <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
        <FormButtonWrapper>
          <Grid>
            <SaveAndCompleteLaterButton />
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
    </Grid>
  );
}
